import type {
  ContentLibraryInput,
  DSLFile,
  FormComponent,
  FormContextRequirements,
  FormDSL,
  FormDSLContext,
} from '@commenda/types';
import { DSLContextMarker } from '../dsl-constants';
import { detectCommendaOSContextNeeds } from './detectCommendaOSContextNeeds';
import { walkComponents } from './dsl-traversal';

/**
 * Whether a Content Library input's bound value is currently unavailable:
 * - source 'context': the bound context path has no truthy value in dslContext
 * - source 'field': the bound field has no truthy value in formData
 * - source 'static' (or unbound): never missing
 */
function isInputValueMissing(
  input: ContentLibraryInput,
  dslContext: FormDSLContext | undefined,
  formData: Record<string, unknown>
): boolean {
  switch (input.source) {
    case 'context':
      return Boolean(input.contextPath && !dslContext?.[input.contextPath]);
    case 'field':
      return Boolean(input.fieldId && !formData[input.fieldId]);
    default:
      return false;
  }
}

/**
 * First `required` Content Library input whose bound value is missing, or
 * undefined when none are missing. Used by option-driven wrappers (Select,
 * RadioGroup, CheckboxGroup) to build their "Waiting for..." placeholder.
 */
export function findMissingRequiredContentLibraryInput(
  inputs: ContentLibraryInput[] | undefined,
  dslContext: FormDSLContext | undefined,
  formData: Record<string, unknown>
): ContentLibraryInput | undefined {
  return inputs?.find(
    (input) => input.required && isInputValueMissing(input, dslContext, formData)
  );
}

/**
 * Whether any `required` Content Library input is missing its bound value.
 * Non-required inputs never block: the API call proceeds without them.
 */
export function hasMissingRequiredContentLibraryInputs(
  inputs: ContentLibraryInput[] | undefined,
  dslContext: FormDSLContext | undefined,
  formData: Record<string, unknown>
): boolean {
  return (
    findMissingRequiredContentLibraryInput(inputs, dslContext, formData) !==
    undefined
  );
}

/**
 * Whether ANY Content Library input (regardless of `required`) is missing its
 * bound value. DynamicFields semantics: its schema fetch depends on every
 * bound input, so it blocks on all of them — intentionally different from the
 * required-only check used by option-driven wrappers.
 */
export function hasMissingContentLibraryInputValues(
  inputs: ContentLibraryInput[] | undefined,
  dslContext: FormDSLContext | undefined,
  formData: Record<string, unknown>
): boolean {
  return Boolean(
    inputs?.some((input) => isInputValueMissing(input, dslContext, formData))
  );
}

/**
 * Top-level context key for a DynamicFields component whose fieldsSource reads
 * field definitions from dslContext (fieldsSource: { type: 'context', path }).
 * FormComponent.props is untyped (Record<string, unknown>), so narrow
 * structurally instead of asserting.
 */
function getContextFieldsSourceTopLevelKey(
  componentProps: Record<string, unknown> | undefined
): string | undefined {
  const fieldsSource = componentProps?.fieldsSource;
  if (typeof fieldsSource !== 'object' || fieldsSource === null) {
    return undefined;
  }
  if (!('type' in fieldsSource) || !('path' in fieldsSource)) {
    return undefined;
  }
  if (fieldsSource.type !== 'context' || typeof fieldsSource.path !== 'string') {
    return undefined;
  }
  const topLevelKey = fieldsSource.path.split('.')[0];
  return topLevelKey || undefined;
}

function collectContextKeysFromComponent(
  component: FormComponent,
  contextKeys: Set<string>
): void {
  // commendaOS context bindings sourced from the top-level form context.
  // Bindings sourced from other fields (or entity-level context) are satisfied
  // within the form itself and need nothing from the caller.
  const contextBindings = component.dataSource?.commendaOS?.contextBindings ?? [];
  contextBindings.forEach((binding) => {
    if (binding.sourceFieldId === DSLContextMarker.FORM_CONTEXT) {
      contextKeys.add(binding.contextKey);
    }
  });

  // Content Library inputs bound to context. Includes non-required inputs:
  // they still shape the fetched options, and DynamicFields blocks on them.
  const contentLibraryInputs = component.dataSource?.contentLibrary?.inputs ?? [];
  contentLibraryInputs.forEach((input) => {
    if (input.source === 'context' && input.contextPath) {
      contextKeys.add(input.contextPath);
    }
  });

  if (component.component === 'DynamicFields') {
    const contextSourceKey = getContextFieldsSourceTopLevelKey(component.props);
    if (contextSourceKey) {
      contextKeys.add(contextSourceKey);
    }
  }

  // walkComponents visits top-level components only; recurse into templates
  // ourselves (mirrors the backend FormContextRequirementsValidator).
  (component.template ?? []).forEach((nestedComponent) => {
    collectContextKeysFromComponent(nestedComponent, contextKeys);
  });
}

/**
 * Every context key a form needs to render fully, from all binding forms the
 * DSL supports:
 * - component-level Content Library inputs with source 'context'
 * - commendaOS contextBindings sourced from the form context marker
 * - DynamicFields fieldsSource of type 'context' (top-level path segment)
 * - form-level `form.contextRequirements.required`
 * - implicit companyId/corporationId/directorId needs of Commenda OS fields
 *   (via detectCommendaOSContextNeeds)
 *
 * Returns a deduplicated array in stable walk order.
 *
 * Accepts both DSLFile (what the renderer receives) and FormDSL. DSLFile's
 * `form` type omits `contextRequirements`, so it's read structurally below.
 */
export function collectRequiredContextKeys(dsl: DSLFile | FormDSL): string[] {
  const contextKeys = new Set<string>();

  walkComponents(dsl, (component) => {
    collectContextKeysFromComponent(component, contextKeys);
  });

  const formContextRequirements = (
    dsl.form as { contextRequirements?: FormContextRequirements } | undefined
  )?.contextRequirements;
  (formContextRequirements?.required ?? []).forEach((requiredKey) => {
    contextKeys.add(requiredKey);
  });

  // detectCommendaOSContextNeeds only reads steps/modules/components + dataSource,
  // never form.contextRequirements, so the DSLFile→FormDSL widening is safe here.
  const commendaOSNeeds = detectCommendaOSContextNeeds(dsl as FormDSL);
  if (commendaOSNeeds.needsCompany) {
    contextKeys.add('companyId');
  }
  if (commendaOSNeeds.needsCorporation) {
    contextKeys.add('corporationId');
  }
  if (commendaOSNeeds.needsDirector) {
    contextKeys.add('directorId');
  }

  return Array.from(contextKeys);
}
